import { useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { buildQueryForFieldDependentTable } from '../../../utils/metadata/tables';
import { invalidateAndRefetch } from '../../../utils/metadata/cache';
import { liveChangeIncludesId } from '../../../utils/realtime/liveChanges';
import type { LiveChangeEventDetail } from '../../../utils/realtime/liveChanges';

interface UseRecordRealtimeSyncParams {
  tableName: string;
  recordId: number;
  getRecordArgs: any;
  schemaName: string;
  metadata: any;
}

interface UseRecordRealtimeSyncResult {
  isDeleted: boolean;
  showDeletionModal: boolean;
  deletionInfo: {
    deletedAt: Date;
  } | null;
  setShowDeletionModal: Dispatch<SetStateAction<boolean>>;
}

export const useRecordRealtimeSync = ({
  tableName,
  recordId,
  getRecordArgs,
  schemaName,
  metadata
}: UseRecordRealtimeSyncParams): UseRecordRealtimeSyncResult => {
  const [isDeleted, setIsDeleted] = useState(false);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [deletionInfo, setDeletionInfo] = useState<{
    deletedAt: Date;
  } | null>(null);

  useEffect(() => {
    const handleTableChange = async (event: Event) => {
      const detail = (event as CustomEvent<LiveChangeEventDetail>).detail;

      // Same table name can exist in both CRM schemas, ignore the other schema's events
      if (!detail || detail.schema !== schemaName || detail.table !== tableName) return;

      // Check if this event is for the current record
      if (liveChangeIncludesId(detail, recordId)) {
        // Handle DELETE events specially. A bulk delete too large to list its ids is not
        // proof that this record went away; refetching settles it.
        if (detail.eventType === 'DELETE' && detail.ids !== null) {
          setIsDeleted(true);
          setShowDeletionModal(true);
          setDeletionInfo({ deletedAt: new Date() });
          return; // Don't try to refetch deleted record
        }

        // Handle INSERT/UPDATE events
        const tableInfo = metadata?.tableData?.byName?.[tableName];

        const queryBuilder = buildQueryForFieldDependentTable({
          ...getRecordArgs,
          schema: schemaName,
          deep: true, // MUST match the useFieldDependentTable query
          tableInfo: tableInfo as any // Include tableInfo for correct select clause
        });

        invalidateAndRefetch(queryBuilder.query);
      }
    };

    // Listen to all operation types (insert, update, delete)
    const insertEventName = `${tableName}_insert`;
    const updateEventName = `${tableName}_update`;
    const deleteEventName = `${tableName}_delete`;

    globalThis.addEventListener(insertEventName, handleTableChange);
    globalThis.addEventListener(updateEventName, handleTableChange);
    globalThis.addEventListener(deleteEventName, handleTableChange);

    return () => {
      globalThis.removeEventListener(insertEventName, handleTableChange);
      globalThis.removeEventListener(updateEventName, handleTableChange);
      globalThis.removeEventListener(deleteEventName, handleTableChange);
    };
  }, [tableName, recordId, getRecordArgs, schemaName, metadata]);

  return {
    isDeleted,
    showDeletionModal,
    deletionInfo,
    setShowDeletionModal
  };
};
